import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { take, filter } from 'rxjs/operators';

// services
import { EventsService } from '../../core/services/events-service/events.service';
import { ProfileService } from '../../core/services/profile-service/profile.service';

// components
import { AlertComponent, EzValidators } from '../../shared';

// constants
import {
	tshirtSizes,
	CalculateAge,
	ScrollIt,
	scrollItType,
	getPhoneMask,
	stateList,
} from '../../shared';

// interfaces
import { School } from '../interfaces';

@Component({
	selector: 'eze-volunteer-registration',
	templateUrl: './volunteer-registration.component.html',
	styleUrls: ['./volunteer-registration.component.css'],
})
export class VolunteerRegistrationComponent implements OnInit {
	volunteerForm: FormGroup;
	showValidation = false;
	eventId = '';
	successHtml = '';
	hasSelectedPrefill = false;

	@ViewChild(AlertComponent, { static: false })
	alert: AlertComponent;
	@ViewChild('waiverModal', { static: false })
	waiverModal;
	@ViewChild('termsModal', { static: false })
	termsModal;

	shifts = [
		{
			value: 'FRI Shift',
			label: 'Fri, Sept. 20 - PM Shift - SET UP ONLY',
		},
		{
			value: 'AM Shift',
			label: 'Sat, Sept. 21 - AM Shift - 7:00am - 2:00pm',
		},
		{
			value: 'PM Shift',
			label: 'Sat, Sept. 21 - PM Shift - 12:00pm noon - end of event',
		},
		{
			value: 'Full Shift',
			label: 'Sat, Sept. 21 - Full Event Shift',
		},
	];

	assignments = [
		{
			value: 'Set Up',
			display:
				'Set Up - Fri, Sep 21 - PM Shift (Free Sat Admission. No T-Shirt)',
		},
		{
			value: 'School Packet',
			display:
				'School Packet Pickup - AM Shift (Must be combined with another)',
		},
		{
			value: 'Ring',
			display: 'Ring Coordinator',
		},
		{
			value: 'Computer',
			display: 'Computer Operator',
		},
		{
			value: 'Runner',
			display: 'Runner (Holding / Staging / Floor)',
		},
		{
			value: 'Security',
			display: 'Security',
		},
		{
			value: 'Staging',
			display: 'Staging',
		},
		{
			value: 'Medical',
			display: 'Medical / First Aid',
		},
		{
			value: 'Awards',
			display: 'Awards (Awards, Kiddie Corner, Runner)',
		},
		{
			value: 'Clean Up',
			display: 'Clean Up - PM Shift',
		},
		{
			value: 'Special (Internal Assignment Only)',
			display: 'Special (Internal Assignment Only)',
		},
	];

	get shirtSizes(): string[] {
		return tshirtSizes;
	}

	isMinor(formGroup: FormGroup): boolean {
		const birth_date = formGroup.get('birth_date').value;
		return this.calculateAge(new Date(birth_date)) < 18;
	}

	get waiverText(): string {
		return this.eventService.waiver.text;
	}

	get waiverHeader(): string {
		return this.eventService.waiver.header;
	}

	get isClosed(): boolean {
		return this.eventService.isRegistrationClosed('volunteer');
	}

	get eventUrl(): string {
		return this.eventService.eventData.url;
	}

	get profile() {
		return this.profileService.profile;
	}

	get isGuest() {
		return this.profileService.isGuest;
	}

	get stateList(): string[] {
		return stateList;
	}

	get schools(): School[] {
		return this.eventService.schools;
	}

	get message(): string | undefined {
		return (
			this.eventService.eventData.registrations.volunteers &&
			this.eventService.eventData.registrations.volunteers.message
		);
	}

	constructor(
		private fb: FormBuilder,
		@Inject(CalculateAge) private calculateAge,
		@Inject(ScrollIt) private scrollIt: scrollItType,
		private eventService: EventsService,
		private route: ActivatedRoute,
		private profileService: ProfileService
	) {}

	ngOnInit() {
		this.getEventId();
		this.initializeForm();
	}

	getEventId() {
		this.eventId = this.route.snapshot.parent.params.id;
	}

	// documentIdRequired() {
	// 	if (this.eventId !== 'svopen2019') {
	// 		return { document_id: ['', Validators.required] };
	// 	} else {
	// 		return {};
	// 	}
	// }

	initializeForm() {
		this.volunteerForm = this.fb.group(
			{
				// personal information
				name_first: ['', Validators.required],
				name_middle: [''],
				name_last: ['', Validators.required],

				birth_date: ['', Validators.required],

				gender: ['', Validators.required],

				// ...this.documentIdRequired(),

				// contact information
				email: ['', [Validators.required, Validators.email]],
				phone: ['', Validators.required],
				address: ['', Validators.required],
				address_2: [''],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required],

				// misc information
				shirt_size: ['', Validators.required],
				school: this.fb.group(
					{
						id: [''],
						name: [''],
						master: [''],
						address: [''],
						address_2: [''],
						city: [''],
						state: [''],
						zip: [''],
						country: [''],
						phone: [''],
						email: [''],
						url: [''],
					},
					{
						validator: this.schoolRequired,
					}
				),

				// parent information
				parent_name_first: [''],
				parent_name_middle: [''],
				parent_name_last: [''],
				parent_email: [''],
				parent_phone: [''],

				// shifts
				competing: ['', Validators.required],
				limitations: [''],

				// legal stuff and subsrcriptions
				subscription: [false],
				legal_age: [false, Validators.requiredTrue],
				no_crime: [false, Validators.requiredTrue],
				waiver: [false, Validators.requiredTrue],
				terms_and_conditions: [false, Validators.requiredTrue],
				catchall: [false, Validators.requiredTrue],
			},
			{
				validator: [EzValidators.parentRequired],
			}
		);

		// console.log('adding mock data');
		// this.volunteerForm.patchValue(mockData);
	}

	schoolRequired(control: FormGroup) {
		const value = control.value;
		if (value.id === '') {
			return { required: true };
		}

		if (value.id !== '-1') {
			return null;
		}

		const schoolFields = [
			'id',
			'name',
			'master',
			'address',
			'city',
			'state',
			'zip',
			'phone',
		];

		const output = {};

		schoolFields.forEach((field) => {
			if (value[field] === '') {
				output[field] = { required: true };
			}
		});

		if (Object.keys(output).length > 0) {
			return output;
		} else {
			return null;
		}
	}

	parentRequired(control: FormGroup) {
		const value = control.value;
		if (!this.volunteerForm || !this.isMinor(this.volunteerForm)) {
			return null;
		}

		const parentFields = [
			'parent_name_first',
			'parent_name_last',
			'parent_email',
			'parent_phone',
		];

		const output = {};

		parentFields.forEach((field) => {
			if (value[field] === '') {
				output[field] = { required: true };
			}
		});

		if (Object.keys(output).length > 0) {
			return output;
		} else {
			return null;
		}
	}

	onSubmit() {
		this.scrollIt(0);
		if (this.volunteerForm.invalid) {
			this.alert.newAlert('Please fill out required fields', 'danger');
			this.showValidation = true;
			return;
		}

		this.alert.newAlert('Submitting...', 'info');
		this.eventService
			.postVolunteer(this.eventId, this.volunteerForm.value)
			.subscribe(
				(res: any) => {
					if (res && res._html_message) {
						this.successHtml = res._html_message;
					} else {
						this.alert.newAlert('There was an error', 'danger');
					}
					// TODO - add type to res
				},
				(err) => {
					this.alert.newAlert('There was an error', 'danger');
				}
			);
	}

	resetForm() {
		this.volunteerForm.reset();
		this.successHtml = '';
		this.showValidation = false;
		this.hasSelectedPrefill = false;
	}

	openModal(event: Event, modalName: string) {
		event.preventDefault();
		switch (modalName) {
			case 'waiver':
				this.waiverModal.open();
				break;
			case 'terms':
				this.termsModal.open();
				break;
			default:
				break;
		}
	}

	getPhoneMask(formControl) {
		return getPhoneMask(this.volunteerForm.get(formControl).value);
	}

	selectedShifts = {};
	updateShift(event) {
		if (this.selectedShifts[event]) {
			delete this.selectedShifts[event];
		} else {
			this.selectedShifts[event] = true;
		}

		const value = Object.keys(this.selectedShifts).join(',');

		this.volunteerForm.get('shift').setValue(value);
		this.volunteerForm.get('shift').markAsDirty();
		this.volunteerForm.get('shift').markAsTouched();
	}

	selectedAssignments = {};
	updateAssignment(event) {
		if (this.selectedAssignments[event]) {
			delete this.selectedAssignments[event];
		} else {
			this.selectedAssignments[event] = true;
		}

		const value = Object.keys(this.selectedAssignments).join(',');

		this.volunteerForm.get('assignment').setValue(value);
		this.volunteerForm.get('assignment').markAsDirty();
		this.volunteerForm.get('assignment').markAsTouched();
	}
}
